/* ═══════════════════════════════════════════════════════════════════
   [D-599] Свій колір кожного партнера в дашборді (список «🤝 Партнери», «🧩 Функції / дозволи / набори / теми»).

   OWNER 16.10: «кожному партнеру у мене в дашборді задамо різний фоновий колір щоб я на око міг швидко знаходити і розрізняти
   партнерів і так само у карточках - доречі, додай ще кнопку редагування кольору якщо я захочу змінити».
   Колір - у referral_settings, ключ `uniq_partner_color_<id>` = «#rrggbb» (той самий POST /referral/settings, що й інші налаштування
   дашборду: з журналом змін; нового API немає). Партнер без кольору отримує перший ще не зайнятий колір палітри - і він одразу
   зберігається, щоб не мінявся, коли з'являться чи зникнуть інші партнери.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { Box, Popover, Typography, Button } from '@mui/material';
import api from '../api';

export const PALETTE = ['#4fc3f7', '#ffb74d', '#ba68c8', '#81c784', '#f06292', '#4db6ac', '#fff176', '#9575cd',
                        '#e57373', '#aed581', '#64b5f6', '#ff8a65', '#a1887f', '#90a4ae', '#dce775', '#f48fb1'];
const PFX = 'uniq_partner_color_';
const ok = (c) => /^#[0-9a-fA-F]{6}$/.test(String(c || ''));
let cache = null;            // { [pid]: '#rrggbb' } - спільний для всіх сторінок
let loading = null;
const subs = new Set();
const emit = () => subs.forEach(f => f({ ...(cache || {}) }));

function load() {
  if (cache) return Promise.resolve(cache);
  if (!loading) {
    loading = api.get('/referral/settings').then(r => {
      const o = {};
      Object.entries((r && r.data) || {}).forEach(([k, v]) => { if (k.startsWith(PFX) && ok(v)) o[k.slice(PFX.length)] = String(v).toLowerCase(); });
      cache = o; return o;
    }).catch(() => { cache = cache || {}; return cache; }).finally(() => { loading = null; });
  }
  return loading;
}

/** colors: { color(pid), set(pid, hex) }. partners - [{id}] (з /partners): новим партнерам колір призначається й зберігається сам */
export function usePartnerColors(partners) {
  const [m, setM] = useState(cache || {});
  useEffect(() => { const f = (x) => setM(x); subs.add(f); return () => { subs.delete(f); }; }, []);
  const ids = (partners || []).map(p => p.id).sort((a, b) => a - b).join(',');
  useEffect(() => {
    if (!ids) return;
    load().then(o => {
      const used = new Set(Object.values(o));
      const add = {};
      ids.split(',').forEach(id => {
        if (o[id]) return;
        const c = PALETTE.find(x => !used.has(x)) || PALETTE[(Number(id) * 5) % PALETTE.length];
        used.add(c); o[id] = c; add[PFX + id] = c;
      });
      if (Object.keys(add).length) api.post('/referral/settings', add).catch(() => {});
      cache = o; emit();
    });
  }, [ids]);
  const color = (pid) => m[pid] || (cache && cache[pid]) || PALETTE[(Number(pid) * 5) % PALETTE.length];
  const set = (pid, c) => {
    if (!ok(c)) return Promise.reject(new Error('bad color'));
    const v = String(c).toLowerCase();
    return api.post('/referral/settings', { [PFX + pid]: v }).then(() => { cache = { ...(cache || {}), [pid]: v }; emit(); });
  };
  return { color, set };
}

/** кругла кнопка з кольором партнера; дотик - палітра + «свій колір» */
export function ColorPick({ color, onPick, title }) {
  const [el, setEl] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (c) => { setBusy(true); Promise.resolve(onPick && onPick(c)).finally(() => { setBusy(false); setEl(null); }); };
  return (<>
    <Box component="button" type="button" data-pcolor-btn="1" title={title || 'Колір партнера - змінити'}
         onClick={(e) => { e.stopPropagation(); setEl(e.currentTarget); }}
         sx={{ width: 26, height: 26, borderRadius: '50%', border: '2px solid rgba(255,255,255,.75)', bgcolor: color, cursor: 'pointer', p: 0,
               display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flex: 'none', boxShadow: '0 0 0 2px rgba(0,0,0,.35)' }}>
      🎨
    </Box>
    <Popover open={!!el} anchorEl={el} onClose={() => setEl(null)} onClick={(e) => e.stopPropagation()}
             anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { bgcolor: '#16202c', p: 1.4, border: '1px solid rgba(0,229,160,.22)' } }}>
      <Typography sx={{ color: '#a8bece', fontSize: 12, mb: 1 }}>Колір партнера</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 26px)', gap: .8, mb: 1.2 }}>
        {PALETTE.map(c => (
          <Box key={c} component="button" type="button" data-pcolor={c} disabled={busy} onClick={() => pick(c)}
               sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: c, cursor: 'pointer', p: 0,
                     border: c === color ? '3px solid #fff' : '2px solid rgba(255,255,255,.2)' }} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: '#a8bece', fontSize: 12 }}>Свій колір:</Typography>
        <input type="color" data-pcolor-own="1" defaultValue={color} disabled={busy}
               onChange={(e) => { e.target.dataset.v = e.target.value; }}
               style={{ width: 40, height: 26, border: 0, background: 'transparent', cursor: 'pointer' }} />
        <Button size="small" disabled={busy} onClick={(e) => { const i = e.currentTarget.parentElement.querySelector('input[type=color]'); pick(i.dataset.v || i.value); }}
                sx={{ textTransform: 'none', fontSize: 12, color: '#7ffcd3' }}>✓ Зберегти</Button>
      </Box>
    </Popover>
  </>);
}

/** «#rrggbb» + прозорість (0..1) → «#rrggbbaa» */
export const alpha = (hex, a) => (ok(hex) ? hex + Math.round(Math.max(0, Math.min(1, a)) * 255).toString(16).padStart(2, '0') : hex);
